import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AmazonInvoice, AmazonInvoiceStatus } from "@front/m19-api-client";
import { AuthService, BillingService } from "@front/m19-services";
import {
  IBadgeComponent,
  IBadgeVariants,
  IButtonDirective,
  IIconComponent,
  ITableTdDirective,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { saveAs } from "file-saver";
import { DateTime } from "luxon";

type InvoiceStatus = {
  label: string;
  color: IBadgeVariants["color"];
};

@Component({
  selector: "[app-amazon-invoices-row]",
  imports: [TranslocoDirective, ITableTdDirective, IBadgeComponent, CommonModule, IButtonDirective, IIconComponent],
  templateUrl: "./amazon-invoices-row.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmazonInvoicesRowComponent {
  private readonly authService = inject(AuthService);
  private readonly billingService = inject(BillingService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  public readonly amazonInvoice = input.required<AmazonInvoice>();
  public readonly organizationId = input.required<number>();

  readonly locale = toSignal(this.authService.userLocale$);

  readonly invoiceStatus = computed<InvoiceStatus>(() => {
    const invoice = this.amazonInvoice();
    if (invoice.status === AmazonInvoiceStatus.PAID_IN_FULL) {
      return {
        label: this.translocoService.translate("amazon-invoices.paid_in_full"),
        color: "green",
      };
    }
    if (invoice.status === AmazonInvoiceStatus.WRITTEN_OFF) {
      return {
        label: this.translocoService.translate("amazon-invoices.written_off"),
        color: "gray",
      };
    }
    const overdue = DateTime.fromISO(invoice.dueDate) < DateTime.now();
    if (overdue) {
      return {
        label: this.translocoService.translate("amazon-invoices.overdue"),
        color: "red",
      };
    }
    return {
      label: this.translocoService.translate(`amazon-invoices.${invoice.status.toLocaleLowerCase()}`),
      color: "orange",
    };
  });

  readonly downloading = signal(false);

  downloadInvoice() {
    this.downloading.set(true);
    const invoice = this.amazonInvoice();
    this.billingService.downloadAmazonInvoice(invoice).subscribe({
      next: (data: Blob) => {
        saveAs(data, invoice.invoiceId + ".pdf");
        this.downloading.set(false);
      },
      error: (e) => {
        this.toastrService.error(e, this.translocoService.translate("amazon-invoices.invoice_download_error"));
        this.downloading.set(false);
      },
    });
  }
}
